import React, { useCallback, useMemo } from 'react';
/* eslint-disable react-hooks/exhaustive-deps */
export default function useEventDetailsAssets(context) {
  const {
    BANNER_IMAGE_PATH,
    FLYER_IMAGE_PATH,
    UNSUPPORTED_IMAGE_MESSAGE,
    bannerFileInputRef,
    bannerUrl,
    canEditEventInfo,
    confirmDialog,
    currentFlyerSide,
    deleteAdminEventBanner,
    deleteAdminEventFlyer,
    deleteEventPerformanceFlyer,
    eventData,
    eventId,
    eventReadOnlyTitle,
    flyerFileInputsRef,
    flyerUrls,
    inheritedAssetSource,
    isSupportedImageFile,
    performanceFlyerInputRef,
    selectedMultiPerformance,
    setBannerActionLoading,
    setBannerUrl,
    setCurrentFlyerSide,
    setEventData,
    setFlyerActionLoading,
    setFlyerUrls,
    setPerformanceFlyerLoading,
    setShowFlyerPopup,
    showFlyerPopup,
    toUrl,
    toastError,
    toastSuccess,
    uploadAdminEventBanner,
    uploadAdminEventFlyer,
    uploadEventPerformanceFlyer,
  } = context;
    const usesParentAssets = useMemo(() => {
      if (!eventData) return false;
      return (
        eventData.performance_flag === 'additional' &&
        eventData.use_existing_flyers &&
        eventData.original_event_uuid
      );
    }, [eventData]);

    const flyerSideText = {
      front: '表面',
      back: '裏面',
    };

    const openFlyerPreview = useCallback(
      (side) => {
        if (!flyerUrls[side]) return;
        setCurrentFlyerSide(side);
        setShowFlyerPopup(true);
      },
      [flyerUrls],
    );

    const renderInheritanceNotice = (assetLabel) => {
      if (!usesParentAssets) return null;
      const name = inheritedAssetSource?.name || '親イベント';
      const performance = (inheritedAssetSource?.performance || '').trim();
      const labelText = performance ? `${name}『${performance}』` : name;
      const link = inheritedAssetSource?.uuid
        ? `/admin/workspaces/event-operations/events/${inheritedAssetSource.uuid}`
        : null;
      return (
        <p className="asset-inherit-notice">
          親イベント{' '}
          {link ? (
            <a href={link} target="_blank" rel="noopener noreferrer">
              {labelText}
            </a>
          ) : (
            <strong>{labelText}</strong>
          )}
          の{assetLabel}を参照しています。
        </p>
      );
    };

    const promptFlyerUpload = (side) => {
      if (!canEditEventInfo) {
        toastError(eventReadOnlyTitle);
        return;
      }
      if (usesParentAssets) {
        toastError('親イベントのフライヤーを使用しているため編集できません。');
        return;
      }
      flyerFileInputsRef.current[side]?.click();
    };

    const handleFlyerInputChange = (side, event) => {
      if (!canEditEventInfo) {
        event.target.value = '';
        toastError(eventReadOnlyTitle);
        return;
      }
      const file = event.target.files?.[0];
      if (!file) return;
      if (!isSupportedImageFile(file)) {
        toastError(UNSUPPORTED_IMAGE_MESSAGE);
        event.target.value = '';
        return;
      }
      uploadFlyerForSide(side, file);
      event.target.value = '';
    };

    const uploadFlyerForSide = async (side, file) => {
      if (!canEditEventInfo) {
        toastError(eventReadOnlyTitle);
        return;
      }
      if (usesParentAssets) {
        toastError('親イベントのフライヤーを使用しているため編集できません。');
        return;
      }
      setFlyerActionLoading((prev) => ({ ...prev, [side]: true }));
      try {
        const formData = new FormData();
        formData.append('image', file);
        const filename = await uploadAdminEventFlyer(eventId, side, formData);
        const nextUrl = filename ? toUrl(FLYER_IMAGE_PATH, filename) : null;
        setFlyerUrls((prev) => ({ ...prev, [side]: nextUrl }));
        setEventData((prev) =>
          prev
            ? {
                ...prev,
                [side === 'front' ? 'flyer_front_url' : 'flyer_back_url']: filename,
              }
            : prev,
        );
        toastSuccess(`${flyerSideText[side]}のフライヤーを更新しました`);
      } catch (err) {
        toastError(err.message || 'フライヤーの更新に失敗しました');
      } finally {
        setFlyerActionLoading((prev) => ({ ...prev, [side]: false }));
      }
    };

    const handleFlyerDelete = async (side) => {
      if (!canEditEventInfo) {
        toastError(eventReadOnlyTitle);
        return;
      }
      if (usesParentAssets) {
        toastError('親イベントのフライヤーを使用しているため編集できません。');
        return;
      }
      if (!flyerUrls[side]) {
        toastError(`${flyerSideText[side]}のフライヤーは登録されていません`);
        return;
      }
      const confirmed = (await confirmDialog({ message: `${flyerSideText[side]}のフライヤーを削除しますか？`, danger: true }));
      if (!confirmed) return;
      setFlyerActionLoading((prev) => ({ ...prev, [side]: true }));
      try {
        await deleteAdminEventFlyer(eventId, side);
        setFlyerUrls((prev) => ({ ...prev, [side]: null }));
        setEventData((prev) =>
          prev
            ? {
                ...prev,
                [side === 'front' ? 'flyer_front_url' : 'flyer_back_url']: null,
              }
            : prev,
        );
        if (showFlyerPopup && currentFlyerSide === side) {
          setShowFlyerPopup(false);
        }
        toastSuccess(`${flyerSideText[side]}のフライヤーを削除しました`);
      } catch (err) {
        toastError(err.message || 'フライヤーの削除に失敗しました');
      } finally {
        setFlyerActionLoading((prev) => ({ ...prev, [side]: false }));
      }
    };

    const promptPerformanceFlyerUpload = () => {
      if (!canEditEventInfo) {
        toastError(eventReadOnlyTitle);
        return;
      }
      if (!selectedMultiPerformance?.id) {
        toastError('公演を選択してください');
        return;
      }
      performanceFlyerInputRef.current?.click();
    };

    const handlePerformanceFlyerInputChange = (event) => {
      if (!canEditEventInfo) {
        event.target.value = '';
        toastError(eventReadOnlyTitle);
        return;
      }
      const file = event.target.files?.[0];
      if (!file) return;
      if (!isSupportedImageFile(file)) {
        toastError(UNSUPPORTED_IMAGE_MESSAGE);
        event.target.value = '';
        return;
      }
      uploadPerformanceFlyer(file);
      event.target.value = '';
    };

    const updatePerformanceFlyerInState = (performanceId, filename) => {
      setEventData((prev) => {
        if (!prev || !Array.isArray(prev.performances)) return prev;
        return {
          ...prev,
          performances: prev.performances.map((performance) =>
            String(performance?.id || '') === String(performanceId || '')
              ? { ...performance, flyer_front_url: filename || null }
              : performance
          ),
        };
      });
    };

    const uploadPerformanceFlyer = async (file) => {
      if (!canEditEventInfo) {
        toastError(eventReadOnlyTitle);
        return;
      }
      const performanceId = selectedMultiPerformance?.id;
      if (!performanceId) {
        toastError('公演を選択してください');
        return;
      }
      setPerformanceFlyerLoading(true);
      try {
        const formData = new FormData();
        formData.append('image', file);
        const filename = await uploadEventPerformanceFlyer(eventId, performanceId, formData);
        updatePerformanceFlyerInState(performanceId, filename);
        toastSuccess('公演フライヤーを更新しました');
      } catch (err) {
        toastError(err.message || '公演フライヤーの更新に失敗しました');
      } finally {
        setPerformanceFlyerLoading(false);
      }
    };

    const handlePerformanceFlyerDelete = async () => {
      if (!canEditEventInfo) {
        toastError(eventReadOnlyTitle);
        return;
      }
      const performanceId = selectedMultiPerformance?.id;
      if (!performanceId || !selectedMultiPerformance?.flyer_front_url) {
        toastError('削除できる公演フライヤーがありません');
        return;
      }
      const confirmed = (await confirmDialog({ message: '選択中の公演フライヤーを削除しますか？', danger: true }));
      if (!confirmed) return;
      setPerformanceFlyerLoading(true);
      try {
        await deleteEventPerformanceFlyer(eventId, performanceId);
        updatePerformanceFlyerInState(performanceId, null);
        toastSuccess('公演フライヤーを削除しました');
      } catch (err) {
        toastError(err.message || '公演フライヤーの削除に失敗しました');
      } finally {
        setPerformanceFlyerLoading(false);
      }
    };

    const promptBannerUpload = () => {
      if (!canEditEventInfo) {
        toastError(eventReadOnlyTitle);
        return;
      }
      if (usesParentAssets) {
        toastError('親イベントのバナーを使用しているため編集できません。');
        return;
      }
      bannerFileInputRef.current?.click();
    };

    const handleBannerInputChange = (event) => {
      if (!canEditEventInfo) {
        event.target.value = '';
        toastError(eventReadOnlyTitle);
        return;
      }
      const file = event.target.files?.[0];
      if (!file) return;
      if (!isSupportedImageFile(file)) {
        toastError(UNSUPPORTED_IMAGE_MESSAGE);
        event.target.value = '';
        return;
      }
      uploadBannerImage(file);
      event.target.value = '';
    };

    const uploadBannerImage = async (file) => {
      if (!canEditEventInfo) {
        toastError(eventReadOnlyTitle);
        return;
      }
      if (usesParentAssets) {
        toastError('親イベントのバナーを使用しているため編集できません。');
        return;
      }
      setBannerActionLoading(true);
      try {
        const formData = new FormData();
        formData.append('image', file);
        const filename = await uploadAdminEventBanner(eventId, formData);
        const nextUrl = filename ? toUrl(BANNER_IMAGE_PATH, filename) : null;
        setBannerUrl(nextUrl);
        setEventData((prev) => (prev ? { ...prev, banner_image_url: filename } : prev));
        toastSuccess('バナーを更新しました');
      } catch (err) {
        toastError(err.message || 'バナーの更新に失敗しました');
      } finally {
        setBannerActionLoading(false);
      }
    };

    const handleBannerDelete = async () => {
      if (!canEditEventInfo) {
        toastError(eventReadOnlyTitle);
        return;
      }
      if (usesParentAssets) {
        toastError('親イベントのバナーを使用しているため編集できません。');
        return;
      }
      if (!bannerUrl) {
        toastError('削除するバナーがありません');
        return;
      }
      const confirmed = (await confirmDialog({ message: 'バナーを削除しますか？', danger: true }));
      if (!confirmed) return;
      setBannerActionLoading(true);
      try {
        await deleteAdminEventBanner(eventId);
        setBannerUrl(null);
        setEventData((prev) => (prev ? { ...prev, banner_image_url: null } : prev));
        toastSuccess('バナーを削除しました');
      } catch (err) {
        toastError(err.message || 'バナーの削除に失敗しました');
      } finally {
        setBannerActionLoading(false);
      }
    };

    // ── メモ化計算（フック：順序固定）────────────────────────────────────
    // ジャンル辞書
  return {
    usesParentAssets,
    flyerSideText,
    openFlyerPreview,
    renderInheritanceNotice,
    promptFlyerUpload,
    handleFlyerInputChange,
    handleFlyerDelete,
    promptPerformanceFlyerUpload,
    handlePerformanceFlyerInputChange,
    handlePerformanceFlyerDelete,
    promptBannerUpload,
    handleBannerInputChange,
    handleBannerDelete,
  };
}